import _ from 'lodash';
import React from 'react';
import { SIDE } from './Points/constants';

const nearestAreaTolerance = 40;

function isCollide(a: Pick<DOMRect, 'x' | 'y'>, b: Pick<DOMRect, 'x' | 'y' | 'width' | 'height'>) {
	const c1 = _.inRange(a.x, b.x - nearestAreaTolerance, b.x + b.width + nearestAreaTolerance);
	const c2 = _.inRange(a.y, b.y - nearestAreaTolerance, b.y + b.height + nearestAreaTolerance);

	return c1 && c2;
}

type SideParam = (typeof SIDE)[keyof typeof SIDE] | '';

interface Coordinates {
	x: number;
	y: number;
}

interface Point extends Coordinates {
	side: SideParam;
}

const distance = (position: Coordinates, destination: Coordinates) => {
	const diffX = position.x - destination.x;
	const diffY = position.y - destination.y;
	return Math.sqrt(diffX * diffX + diffY * diffY);
};

const getNearestPoint = (points: Point[], point: Coordinates) =>
	points.reduce<(Point & { distance: number }) | undefined>((prev, curr) => {
		const distToCur = distance(point, curr);
		if (!prev) return { ...curr, distance: distToCur };
		return Math.abs(distToCur) < Math.abs(prev.distance) && Math.abs(distToCur)
			? { ...curr, distance: distToCur }
			: prev;
	}, undefined);

type Props = {
	selectionCoords: {
		top: number;
		left: number;
		width: number;
		height: number;
		position: string;
	};
	onNearestPoint: (side: SideParam) => void;
};

class NearestPoint extends React.Component<Props> {
	componentDidMount() {
		document.addEventListener('mousemove', this.onMouseMove);
	}

	componentWillUnmount() {
		document.removeEventListener('mousemove', this.onMouseMove);
	}

	onMouseMove = event => {
		const { selectionCoords } = this.props;

		const mouse = {
			x: event.pageX - (selectionCoords.position === 'absolute' ? 0 : document.documentElement.scrollLeft),
			y: event.pageY - (selectionCoords.position === 'absolute' ? 0 : document.documentElement.scrollTop),
		};

		const pointsRect = {
			x: selectionCoords.left,
			y: selectionCoords.top,
			width: selectionCoords.width,
			height: selectionCoords.height,
		};

		const x = {
			left: pointsRect.x,
			center: pointsRect.x + pointsRect.width / 2,
			right: pointsRect.x + pointsRect.width,
		};

		const y = {
			top: pointsRect.y,
			center: pointsRect.y + pointsRect.height / 2,
			bottom: pointsRect.y + pointsRect.height,
		};

		const points: { side: (typeof SIDE)[keyof typeof SIDE]; x: number; y: number }[] = [
			{ side: SIDE.top, x: x.center, y: y.top },
			{ side: SIDE.bottom, x: x.center, y: y.bottom },
			{ side: SIDE.right, x: x.right, y: y.center },
			{ side: SIDE.rightTop, x: x.right, y: y.top },
			{ side: SIDE.rightBottom, x: x.right, y: y.bottom },
			{ side: SIDE.left, x: x.left, y: y.center },
			{ side: SIDE.leftTop, x: x.left, y: y.top },
			{ side: SIDE.leftBottom, x: x.left, y: y.bottom },
		];

		let nearestPoint: { side: SideParam } = { side: '' };
		if (isCollide(mouse, pointsRect)) {
			const point = getNearestPoint(points, mouse);
			const toleranceBase = 40;
			const toleranceFactor = 0.2;
			const nearestPointTolerance = Math.max(
				selectionCoords.width <= toleranceBase
					? selectionCoords.width * toleranceFactor
					: selectionCoords.height <= toleranceBase
						? selectionCoords.height * toleranceFactor
						: toleranceBase,
				2
			);
			if (point && Math.abs(point.distance) <= nearestPointTolerance) {
				nearestPoint = point;
			}
		}
		this.props.onNearestPoint(nearestPoint.side);
	};

	render() {
		return null;
	}
}

export default NearestPoint;
